<script lang="ts">
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import dayjs from 'dayjs';
import { defineComponent, inject, PropType } from 'vue';

import { ShippingOffer } from '@/api/shippingCalculator';
import money from '@/filters/money';
import { calculateDaysBetweenDates, DateString } from '@/utils/dateTime';

export default defineComponent({
  name: 'ShippingOffer',
  props: {
    highlight: { required: false, type: Boolean, default: false },
    isDigital: { required: false, type: Boolean, default: false },
    isPickup: { required: false, type: Boolean, default: false },
    shippingOffer: { required: true, type: Object as PropType<ShippingOffer> },
  },
  setup() {
    const isAdmin = inject('isAdmin', false);
    const calculateArrivalDay = ({
      arrival,
      arrivalWindow,
    }: {
      arrival: DateString;
      arrivalWindow: DateString[] | null;
    }): string => {
      if (arrivalWindow?.length) {
        const earliest = dayjs(arrivalWindow[0], 'YYYY-MM-DD');
        const latest = dayjs(arrivalWindow[1], 'YYYY-MM-DD');

        return `${earliest.format('ddd')}, ${earliest.format('MMM D')} - ${latest.format(
          'ddd',
        )}, ${latest.format('MMM D')}`;
      }

      let arrivalDay;
      const arrivalDate = dayjs(arrival, 'YYYY-MM-DD');
      const todaysDate = dayjs().format('YYYY-MM-DD') as DateString;
      const daysfromArrival = calculateDaysBetweenDates(todaysDate, arrival);

      switch (daysfromArrival) {
        case 0:
          arrivalDay = 'Today';
          break;
        case 1:
          arrivalDay = 'Tomorrow';
          break;
        default:
          arrivalDay = arrivalDate.format('dddd');
          break;
      }
      return `${arrivalDay}, ${arrivalDate.format('MMM D')}`;
    };

    return {
      calculateArrivalDay,
      dollars,
      isAdmin,
      money,
    };
  },
});
</script>

<template>
  <!-- eslint-disable-next-line max-len -->
  <div
    class="flex items-center justify-between text-sm font-normal leading-4 md:leading-5 md:text-base font-proxima-nova"
  >
    <template v-if="isDigital">
      <div>
        <div class="font-bold">Arrives Immediately</div>
        <div class="text-xs leading-4">Digital Delivery</div>
      </div>
      <div class="font-bold">FREE</div>
    </template>
    <template v-else-if="isPickup">
      <div>
        <div class="font-bold">
          Ready {{ calculateArrivalDay({ arrival: shippingOffer.latestArrivalOn }) }}
        </div>
        <div class="text-xs leading-4">Customer Pickup</div>
      </div>
      <div class="font-bold">FREE</div>
    </template>
    <template v-else>
      <div>
        <div :class="{ 'font-bold': highlight }">
          Arrives
          {{
            calculateArrivalDay({
              arrival: shippingOffer.latestArrivalOn,
              arrivalWindow: shippingOffer.arrivalWindowDates,
            })
          }}
        </div>
        <div class="text-xs leading-4">
          {{ shippingOffer.name }}
        </div>
        <div v-if="isAdmin" class="text-nuts-red-800">
          <div>
            {{ shippingOffer.shipmentPickup.carrierCode }}
          </div>
          <div class="text-xs">
            {{ shippingOffer.shipmentPickup.carrierPickup }}
          </div>
        </div>
      </div>
      <div :class="{ 'font-bold': highlight }">
        {{ dollars(shippingOffer.price) > 0 ? money(shippingOffer.price) : 'FREE' }}
      </div>
    </template>
  </div>
</template>
